/**
 * Backend URL helpers. All URLs are derived from `VITE_*` env vars so we don't
 * leak prod hosts into the codebase.
 */
const APIM = import.meta.env.VITE_RAPTOR_APIM_BASE_URL;
const TOKEN_SVC = import.meta.env.VITE_TOKEN_SERVICE_PATH;
const RUM = import.meta.env.VITE_RUM_PATH;

export const Endpoints = {
  /** RUM client list with favorites flag. */
  rumUserClients: () => `${APIM}${TOKEN_SVC}/v1/authorization/clients`,

  /** RUM user profile/details by OID. Contains legacy `userType` (1 internal, 2 external). */
  rumUserDetails: (oid: string) => `${APIM}${TOKEN_SVC}/v1/users/${encodeURIComponent(oid)}`,

  /** AD avatar lookup by email. `internalAD` = is internal user. */
  adAvatar: (internalAD: boolean) =>
    `${APIM}${TOKEN_SVC}/v1/ad/getuseravatar?internalAD=${internalAD}`,

  /** RUM roles for profile page. */
  rumRolesForProfile: (clientGuid: string) =>
    `${APIM}${TOKEN_SVC}/v1/users/GetRumRolesForProfilePage?clientGuid=${encodeURIComponent(clientGuid)}`,

  /** Toggle favorite on a client. */
  favoriteClient: (clientId: string) =>
    `${APIM}${RUM}/v1/core/users/AddOrUpdateFavoriteClient/${encodeURIComponent(clientId)}`,

  /**
   * Portal app tiles for a client — the real, per-client app launcher feed.
   * Mirrors legacy `HeaderWorkspacesService.getAppTiles`
   * (`raptorApimBaseUrl + AppTilesPath + '/v1/Apps/GetTiles'`, params
   * `clientId` + `moduleName=Portal`).
   */
  appTiles: (clientId: string) =>
    `${APIM}/portalapps/api/v1/Apps/GetTiles?clientId=${encodeURIComponent(clientId)}&moduleName=Portal`,

  /**
   * Portal ADMIN app tiles for a client — the admin-app launcher feed. Mirrors
   * legacy `AuthorizeService.getAdminApps` (`raptorApimBaseUrl + AdminTilesPath
   * + '/v1/Admin/GetTiles'`, where `AdminTilesPath` = `/portaladmin/api`, params
   * `clientId` + `moduleName=Portal`). Folded into the load registry so admin
   * routes resolve the right loader (iframe / MF / external).
   */
  adminTiles: (clientId: string) =>
    `${APIM}/portaladmin/api/v1/Admin/GetTiles?clientId=${encodeURIComponent(clientId)}&moduleName=Portal`,

  /**
   * Role-screen mapping (v2) — which app screens the signed-in user may open.
   * Mirrors legacy `HeaderWorkspacesService.getRoleMapping` with
   * `enableV2RoleMaping` on (`raptorApimBaseUrl + roleScreenPath +
   * '/v1/RoleScreenMapping/GetRolesMapping'`, param `source=Portal`).
   */
  roleScreenMapping: () =>
    `${APIM}/role-scr-mgmt/api/v1/RoleScreenMapping/GetRolesMapping?source=Portal`,

  // ── SignalR (real-time portal push) — legacy `SignalRService`. All under
  // `role-scr-mgmt/api`, same RUM-protected origin (Raptor-Authorization). ──
  /** Negotiate a hub URL + access token (PUT, body = userOid JSON string). */
  signalRNegotiate: () => `${APIM}/role-scr-mgmt/api/v1/SignalR/Negotiatesignalr`,
  /** Add a connection to client/role groups (POST {connectionId, groups}). */
  signalRAddToGroups: () => `${APIM}/role-scr-mgmt/api/v1/SignalR/AddConnectionToGroups`,
  /** Remove a connection from specific groups (DELETE {connectionId, groups}). */
  signalRRemoveFromGroups: () => `${APIM}/role-scr-mgmt/api/v1/SignalR/RemoveConnectionFromGroups`,
  /** Remove a connection from all its groups (DELETE {connectionId}). */
  signalRRemoveFromAllGroups: () =>
    `${APIM}/role-scr-mgmt/api/v1/SignalR/RemoveConnectionFromAllGroups`,

  /**
   * Status-page incident feed (initial load; live updates arrive via the
   * SignalR `portalincidentstatusmethod` hub). Mirrors legacy
   * `IncidentStatusService.fetchInitialIncidents`
   * (`raptorApimBaseUrl + incidentApiPath('/incidentstatus') +
   * '/GetIncidentStatusUpdates'`). RUM-protected (see raptor-auth-map).
   */
  incidentStatus: () => `${APIM}/incidentstatus/GetIncidentStatusUpdates`,

  /** My Teams contacts for a client (legacy `HeaderMyteamsService.getContacts` v2). */
  myTeamsContacts: () => `${APIM}/myteams/api/v1/clientteams/client/members`,

  // ── Chatbot (traditional inline panel) — legacy `CHAT_API` ──
  /** POST chat history → get OpenAI response. Legacy `GET_AnswerFromOpenAIv1`. */
  chatbotAnswer: () => `${APIM}/portaladmin/api/v1/OpenAI/GetAnswerFromOpenAIv1`,
  /** PUT like/dislike feedback. Legacy `PUT_UpdateOpenAILogv1`. */
  chatbotFeedback: () => `${APIM}/portaladmin/api/v1/OpenAI/UpdateOpenAILogv1`,

  /** Update the user's most recently selected client (legacy `UserInformationService.updateRecentClient`). */
  updateRecentClient: (clientId: string) =>
    `${APIM}${TOKEN_SVC}/v1/users/UpdateRecentClient/${encodeURIComponent(clientId)}`,
} as const;
